import { Order } from '../models/order.model';
import { toMillis } from './timestamp.util';

/**
 * How far back an unacknowledged ready item still alerts. Without a limit, the
 * first deploy (and any console opened after a quiet shift) would demand an
 * acknowledgement for every item nobody ever acknowledged.
 */
export const READY_ALERT_WINDOW_MS = 2 * 60 * 60 * 1000;

export interface ReadyAlert {
  key: string;
  orderId: string;
  lineId: string;
  tableId: string;
  name: string;
  quantity: number;
  kotNo: number | null;
  imageUrl: string | null;
  readyByName: string | null;
  readyAtMs: number;
}

export interface ServedAnnouncement {
  key: string;
  tableId: string;
  name: string;
  quantity: number;
  servedByName: string | null;
  servedAtMs: number;
}

/**
 * Items the kitchen marked ready that this manager still has to acknowledge,
 * oldest first. Falls away by itself once someone acknowledges it or the
 * waiter serves it. Someone never has to acknowledge their own tap, so a
 * whole-ticket Mark ready from this console raises nothing here.
 */
export function pendingReadyAlerts(orders: readonly Order[], myUid: string | null, nowMs: number): ReadyAlert[] {
  const alerts: ReadyAlert[] = [];
  for (const order of orders) {
    if (order.status !== 'open') continue;
    for (const line of order.lines ?? []) {
      if (line.status !== 'active' || line.servedAt || line.readyAckAt) continue;
      const readyAtMs = toMillis(line.readyAt);
      if (readyAtMs === null || nowMs - readyAtMs > READY_ALERT_WINDOW_MS) continue;
      if (line.readyBy && line.readyBy === myUid) continue;
      alerts.push({
        key: `${order.id}:${line.lineId}`,
        orderId: order.id,
        lineId: line.lineId,
        tableId: order.tableId,
        name: line.name,
        quantity: line.quantity,
        kotNo: line.kotNo ?? null,
        imageUrl: line.imageUrl ?? null,
        readyByName: line.readyByName ?? null,
        readyAtMs,
      });
    }
  }
  return alerts.sort((a, b) => a.readyAtMs - b.readyAtMs);
}

/**
 * Items served since this console opened and not announced yet, oldest first.
 * Anchored on `sinceMs` so a reload never re-reads the shift's served items
 * aloud, and skips the manager's own tap: they already know.
 */
export function newServedAnnouncements(
  orders: readonly Order[],
  myUid: string | null,
  sinceMs: number,
  alreadyAnnounced: ReadonlySet<string>
): ServedAnnouncement[] {
  const fresh: ServedAnnouncement[] = [];
  for (const order of orders) {
    for (const line of order.lines ?? []) {
      if (line.status !== 'active') continue;
      const servedAtMs = toMillis(line.servedAt);
      if (servedAtMs === null || servedAtMs < sinceMs) continue;
      const key = `${order.id}:${line.lineId}`;
      if (alreadyAnnounced.has(key) || (line.servedBy && line.servedBy === myUid)) continue;
      fresh.push({
        key,
        tableId: order.tableId,
        name: line.name,
        quantity: line.quantity,
        servedByName: line.servedByName ?? null,
        servedAtMs,
      });
    }
  }
  return fresh.sort((a, b) => a.servedAtMs - b.servedAtMs);
}

function spokenItem(name: string, quantity: number): string {
  return quantity > 1 ? `${quantity} ${name}` : name;
}

/** "Table 5, 2 Masala Dosa is ready." */
export function readySpeech(alert: Pick<ReadyAlert, 'name' | 'quantity'>, tableLabel: string): string {
  return `Table ${tableLabel}, ${spokenItem(alert.name, alert.quantity)} is ready.`;
}

/** "Table 5, Masala Dosa served by Anil." */
export function servedSpeech(item: Pick<ServedAnnouncement, 'name' | 'quantity' | 'servedByName'>, tableLabel: string): string {
  const by = item.servedByName ? ` by ${item.servedByName}` : '';
  return `Table ${tableLabel}, ${spokenItem(item.name, item.quantity)} served${by}.`;
}
